import {
  createContext,
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";

/**
 * マイページ全体の通知 (トースト)。フォーム外・非同期の失敗/成功をユーザに知らせる横断的な受け皿。
 * フォームのフィールド検証エラーは従来通り各フォームのインライン表示、システム/API 失敗はここで
 * トーストを出す、という住み分け。表示部 (Toaster) は presentational にして Storybook で単体確認できる。
 */

export type ToastKind = "error" | "success" | "info";
export type Toast = { id: number; kind: ToastKind; message: string };

const AUTO_DISMISS_MS = 6000;

type NotifierContextValue = {
  /** トーストを 1 つ出す。id は内部採番。 */
  notify: (input: { kind: ToastKind; message: string }) => void;
};

const NotifierContext = createContext<NotifierContextValue | null>(null);

/** フォーム等から `notify({ kind, message })` を呼ぶための hook。Provider 配下でのみ有効。 */
export const useNotifier = (): NotifierContextValue => {
  const ctx = useContext(NotifierContext);
  if (!ctx) throw new Error("useNotifier must be used within <NotifierProvider>");
  return ctx;
};

export const NotifierProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const seq = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const notify = useCallback<NotifierContextValue["notify"]>(
    ({ kind, message }) => {
      const id = ++seq.current;
      setToasts((prev) => [...prev, { id, kind, message }]);
      setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
    },
    [dismiss],
  );

  return (
    <NotifierContext.Provider value={{ notify }}>
      {children}
      <Toaster toasts={toasts} onDismiss={dismiss} />
    </NotifierContext.Provider>
  );
};

const KIND_STYLE: Record<ToastKind, { bar: string; icon: string; label: string }> = {
  error: { bar: "bg-red-500", icon: "text-red-500", label: "エラー" },
  success: { bar: "bg-emerald-500", icon: "text-emerald-500", label: "完了" },
  info: { bar: "bg-neutral-400", icon: "text-neutral-400", label: "お知らせ" },
};

/**
 * トーストの表示部 (presentational)。画面下部中央に積み上げる。state を持たないので Storybook で
 * 任意の toasts を渡して見た目を固定できる。
 */
export const Toaster: FC<{ toasts: Toast[]; onDismiss: (id: number) => void }> = ({
  toasts,
  onDismiss,
}) => {
  if (toasts.length === 0) return null;
  return (
    <section
      className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col items-center gap-2 p-4"
      aria-label="通知"
    >
      {toasts.map((t) => {
        const s = KIND_STYLE[t.kind];
        return (
          <div
            key={t.id}
            role={t.kind === "error" ? "alert" : "status"}
            className="pointer-events-auto flex w-full max-w-md overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-lg"
          >
            <span className={`w-1 shrink-0 ${s.bar}`} aria-hidden="true" />
            <div className="flex min-w-0 flex-1 items-start gap-2 px-3.5 py-3">
              <span className={`mt-0.5 shrink-0 font-bold text-sm ${s.icon}`} aria-hidden="true">
                {t.kind === "error" ? "!" : t.kind === "success" ? "✓" : "i"}
              </span>
              <p className="min-w-0 flex-1 whitespace-pre-wrap break-words text-neutral-800 text-sm leading-relaxed">
                {t.message}
              </p>
              <button
                type="button"
                onClick={() => onDismiss(t.id)}
                aria-label="閉じる"
                className="shrink-0 rounded p-0.5 text-neutral-400 text-xs hover:text-neutral-700"
              >
                ✕
              </button>
            </div>
          </div>
        );
      })}
    </section>
  );
};
